import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import MainNavigation, { NavigationSection } from '../components/MainNavigation';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Users from './Tabs/Users';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const { userProfile, profileLoading, profileError } = useUser();
  const [activeSection, setActiveSection] = useState<NavigationSection>('users');

  const handleSectionChange = (section: NavigationSection) => {
    setActiveSection(section);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header with App Bar */}
      <Header />
      
      {/* Main Navigation */}
      <MainNavigation
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
      />
      
      {/* Main content */}
      <Container maxWidth={false} sx={{ mt: 3, mb: 4, px: 3 }}>
        {/* Profile Error Alert */}
        {profileError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {t(profileError, profileError)}
          </Alert>
        )}

        {/* Content based on active section */}
        {/* Always show Users content when it's the only tab, but keep conditional rendering for future tabs */}
        {(activeSection === 'users' || !activeSection) && (
          
            <Users />
            // <LocationTest />
        )}

        {activeSection === 'locations' && (
          <Paper elevation={2} sx={{ p: 2, mb: 4, width: '100%' }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('locations.title', 'Locations Management')}
            </Typography>
            <Typography variant="body1">
              {t('locations.description', 'Manage organization locations and addresses.')}
            </Typography>
          </Paper>
        )}

        {activeSection === 'attachmentTypes' && (
          <Paper elevation={2} sx={{ p: 2, mb: 4, width: '100%' }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('attachmentTypes.title', 'Attachment Types')}
            </Typography>
            <Typography variant="body1">
              {t('attachmentTypes.description', 'Configure and manage attachment types for documents.')}
            </Typography>
          </Paper>
        )}

        {activeSection === 'customValues' && (
          <Paper elevation={2} sx={{ p: 2, mb: 4, width: '100%' }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('customValues.title', 'Custom Values')}
            </Typography>
            <Typography variant="body1">
              {t('customValues.description', 'Define and manage custom field values.')}
            </Typography>
          </Paper>
        )}

        {activeSection === 'productIdentifiers' && (
          <Paper elevation={2} sx={{ p: 2, mb: 4, width: '100%' }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('productIdentifiers.title', 'Product Identifiers')}
            </Typography>
            <Typography variant="body1">
              {t('productIdentifiers.description', 'Manage product identification and classification.')}
            </Typography>
          </Paper>
        )}

        {activeSection === 'documents' && (
          <Paper elevation={2} sx={{ p: 2, mb: 4, width: '100%' }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('documents.title', 'Documents')}
            </Typography>
            <Typography variant="body1">
              {t('documents.description', 'Access and manage system documents and templates.')}
            </Typography>
          </Paper>
        )}
         
      </Container>
      
      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default HomePage;